import { useLocation } from '@tanstack/react-router';
import { useAddInConfig } from './add-in-config.js';

const REPORT_CLAIM_PATH = '/report-claim';

export type ClaimReportGuard =
    | { state: 'loading' }
    | { state: 'error' }
    | { state: 'redirect'; to: string }
    | { state: 'pass' };

/**
 * Policyholders without a KSH contract only get the claim reporting page. The
 * server enforces that on every Schadenmanager endpoint; this guard mirrors it
 * in the UI so the routes never render for them. It fails closed: until the
 * config is known, the caller must not render any protected content.
 */
export function useClaimReportGuard(): ClaimReportGuard {
    const { pathname } = useLocation();
    const { data, isPending } = useAddInConfig();

    if (isPending) {
        return { state: 'loading' };
    }

    if (!data) {
        return { state: 'error' };
    }

    if (data.body.access !== 'claim-report') {
        return { state: 'pass' };
    }

    // '/report-claim/' matches the same route as '/report-claim', and a
    // redirect between the two is a same-location no-op that never resolves.
    const normalizedPathname = pathname.replace(/\/+$/, '') || '/';

    if (normalizedPathname === REPORT_CLAIM_PATH) {
        return { state: 'pass' };
    }

    return { state: 'redirect', to: REPORT_CLAIM_PATH };
}
